import { useEffect, useRef, useState } from "react";
import { samplingMapEmbedUrl } from "@/data/samplingMap";
import { cn } from "@/lib/utils";

type ProvioMapEmbedProps = {
  title: string;
  className?: string;
  iframeClassName?: string;
};

/**
 * Mounts the Provio iframe only when near the viewport so it does not block
 * bfcache (or burn bandwidth) until the user actually reaches the map.
 */
export const ProvioMapEmbed = ({
  title,
  className,
  iframeClassName = "h-[min(65svh,600px)] min-h-[360px] w-full border-0",
}: ProvioMapEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || shouldLoad) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoad]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "overflow-hidden rounded-2xl border border-border/60 bg-card shadow-card sm:rounded-3xl",
        className,
      )}
    >
      {shouldLoad ? (
        <iframe
          src={samplingMapEmbedUrl}
          title={title}
          className={iframeClassName}
          loading="lazy"
          referrerPolicy="no-referrer"
          allow="geolocation"
        />
      ) : (
        <div
          className={cn(iframeClassName, "animate-pulse bg-muted/40")}
          aria-hidden
          role="presentation"
        />
      )}
    </div>
  );
};
